import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Deployment, DeploymentDetail, DeploymentUpdate, AvailableUpdate } from '../types/api';
import Logger from '../utils/logger';
import { showErrorToast, showSuccessToast, parseCommand, commandToString } from '../utils';
import { ErrorMessages, SuccessMessages } from '../constants/messages';
import { ArrowPathIcon, ClockIcon, SignalIcon, HeartIcon, CommandLineIcon, CodeBracketIcon, ChartBarIcon, CubeIcon, CircleStackIcon, TableCellsIcon, InformationCircleIcon, DocumentTextIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import OdooModulesSection from '../components/OdooModulesSection';
import BackupsSection from '../components/BackupsSection';
import SqlEditorSection from '../components/SqlEditorSection';
import StudioSection from '../components/StudioSection';
import KafkaConsoleSection from '../components/KafkaConsoleSection';
import CronScheduleBuilder from '../components/CronScheduleBuilder';
import ConfigFilesEditor from '../components/ConfigFilesEditor';
import AdditionalDatabasesManager from '../components/AdditionalDatabasesManager';
import { DeploymentLogsSection } from '../components/DeploymentLogs';
import MonitoringContent from '../components/MonitoringContent';
import { useTranslation } from 'react-i18next';
import DeleteDeploymentModal from '../components/Deployment/DeleteDeploymentModal';

/**
 * Services dont la suppression detruit des donnees, et que la confirmation doit
 * donc annoncer. Kafka a rejoint la liste avec nubi-kafka 0.4.0 : son volume est
 * desormais supprime avec le cluster, au lieu de survivre orphelin.
 *
 * Keycloak la rejoint pour une raison differente des deux autres : il ne detruit
 * pas un volume qui lui appartient, mais une base sur le PostgreSQL d'un AUTRE
 * deploiement. C'est encore moins devinable — d'ou l'affichage du nom de la base
 * et de son hote, que seule l'API connait (champ keycloak_database de la fiche).
 */
const dataKindDeSuppression = (
  type?: string | null,
): 'postgresql' | 'kafka' | 'keycloak' | 'docuseal' | 'openwebui' | 'managedApp' | null => {
  switch (type?.toLowerCase()) {
    case 'postgresql':
      return 'postgresql';
    case 'kafka':
      return 'kafka';
    case 'keycloak':
      return 'keycloak';
    case 'docuseal':
      // DocuSeal detruit DEUX choses : sa base, comme les autres applications
      // managees, et son espace de stockage — donc les documents signes. C'est
      // la partie que personne ne devine, elle a son propre libelle.
      return 'docuseal';
    case 'openwebui':
      // Meme forme que DocuSeal : base ET bucket — conversations et documents
      // indexes.
      return 'openwebui';
    case 'n8n':
    case 'metabase':
    case 'hasura':
    // `obp` est retire du catalogue mais un deploiement existant peut subsister :
    // supprimer le sien detruit toujours sa base, et l avertissement doit tenir
    // tant qu il en reste un.
    case 'obp':
      // Elles n'avertissaient de RIEN, alors que supprimer l'une d'elles
      // detruit sa base dediee sur le PostgreSQL hote — workflows, tableaux de
      // bord, permissions. Le silence etait le defaut, pas une decision.
      return 'managedApp';
    default:
      return null;
  }
};

/** Dépôt d'images d'une référence, tag retiré. Le ':' ne compte que s'il est
 *  APRÈS le dernier '/', sinon un registre avec port (host:5000/chemin) serait
 *  tronqué en son hôte. */
const depotSansTag = (reference: string): string => {
  const slash = reference.lastIndexOf('/');
  const colon = reference.lastIndexOf(':');
  return colon > slash ? reference.slice(0, colon) : reference;
};

// Type pour les entrées de variables d'environnement
let envVarIdCounter = 0;
const nextEnvVarId = () => `env-${++envVarIdCounter}`;

interface EnvVarEntry {
  /** Chargee depuis la fiche. Pour un secret d'application managee la valeur
   *  arrive VIDE (l'API ne renvoie que les noms) : vide a l'enregistrement
   *  veut dire « inchange ». */
  fromServer?: boolean;
  id: string;
  key: string;
  value: string;
}

const DeploymentManagementPage: React.FC = () => {
  const { t } = (useTranslation as any)('deployments');
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const navigate = useNavigate();
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [deploymentDetails, setDeploymentDetails] = useState<DeploymentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<DeploymentUpdate>({});
  const [environmentId, setEnvironmentId] = useState<string | null>(null);
  const [appHealthStatus, setAppHealthStatus] = useState<'checking' | 'online' | 'starting' | 'offline'>('checking');

  // State pour les variables d'environnement (édition)
  const [envVars, setEnvVars] = useState<EnvVarEntry[]>([]);
  const [secrets, setSecrets] = useState<EnvVarEntry[]>([]);

  // State pour le port de l'application (édition)
  const [appPort, setAppPort] = useState<number | null>(null);

  // State pour le health check path (édition)
  // Vide par défaut = TCP check (vérifie juste que le port est ouvert)
  const [healthCheckPath, setHealthCheckPath] = useState<string>('');

  // State pour l'exposition publique (édition)
  const [exposePublic, setExposePublic] = useState<boolean>(true);

  // State pour la migration (édition)
  const [migrationEnabled, setMigrationEnabled] = useState<boolean>(false);
  const [migrationCommand, setMigrationCommand] = useState<string>('');
  const [migrationBlockOnFailure, setMigrationBlockOnFailure] = useState<boolean>(true);
  const [migrationImageRepository, setMigrationImageRepository] = useState<string>('');
  const [migrationImageTag, setMigrationImageTag] = useState<string>('');
  // Enregistrement en cours : le bouton n'avait aucun etat, donc rien n'empechait
  // deux clics de lancer deux PUT concurrents.
  const [savingEdit, setSavingEdit] = useState<boolean>(false);

  // State pour nginx_custom_config (édition).
  // nginxDirty: true dès que l'user touche au textarea. Sinon on envoie null pour
  // preserve côté backend (évite de wipe la config existante quand l'user save
  // sans avoir touché à nginx.conf).
  const [nginxCustomConfig, setNginxCustomConfig] = useState<string>('');
  const [nginxDirty, setNginxDirty] = useState<boolean>(false);

  // Type de charge, commande de démarrage, planification cron (édition).
  //
  // MÊME DISCIPLINE QUE nginxDirty, et pour une raison mesurée : le deep merge côté API est
  // ADDITIF, donc une clé omise survit dans le values.yaml git. N'envoyer ces champs que si
  // l'utilisateur y a touché évite (a) d'écrire `workloadType: web` dans le values.yaml des
  // 37 apps web qui n'en portent aucun, et (b) d'effacer la commande d'une app qui en a une
  // — il existe en production une app WEB avec `command: [sh, -c, uvicorn ...]`.
  //
  // Et quand il y touche, on envoie les TROIS ensemble : le type seul ne suffit pas, l'API
  // refuse un `cron` sans planification (sans elle le rendu Helm échoue et ArgoCD n'applique
  // plus rien sur l'application).
  // Typé `string` et non l'union des quatre valeurs, A DESSEIN : la fiche lit le values.yaml
  // git, qui a pu être édité à la main. Coercer une valeur inconnue vers 'web' l'AFFICHERAIT
  // comme web — un rapport faux, et le chart, lui, ne traite `default "web"` que pour une
  // valeur vide. En `string`, une valeur exotique ne correspond à aucune carte : elle ne
  // s'affiche pas comme autre chose qu'elle-même.
  const [workloadType, setWorkloadType] = useState<string>('web');
  const [startCommand, setStartCommand] = useState<string>('');
  const [cronSchedule, setCronSchedule] = useState<string>('');
  const [workloadDirty, setWorkloadDirty] = useState<boolean>(false);

  // State pour l'onglet actif
  const [activeTab, setActiveTab] = useState<string>('overview');

  // State pour Go Live (available updates)
  const [availableUpdates, setAvailableUpdates] = useState<AvailableUpdate[]>([]);
  const [selectedUpdateIdx, setSelectedUpdateIdx] = useState<number>(0);
  const [goingLive, setGoingLive] = useState(false);
  const [currentTag, setCurrentTag] = useState<string | null>(null);

  useEffect(() => {
    // Reset Go Live state when switching deployments
    setAvailableUpdates([]);
    setSelectedUpdateIdx(0);

    const fetchDeployment = async () => {
      try {
        // Charger les détails complets depuis l'endpoint détaillé
        const details = await apiService.getDeploymentDetails(deploymentId!);
        setDeploymentDetails(details);
        
        // Créer un objet déploiement minimal avec les infos des détails disponibles
        const deployment = {
          id: deploymentId!,
          nom: details.application_name,
          type_application: '', // Sera rempli par getApplicationType()
          statut_deploiement: details.status,
          cpu_requis: details.cpu_required,
          ram_requise: details.ram_required,
          storage_requis: details.storage_required,
          replicas: details.replicas,
          repo_url: details.repository_url,
          image_registry_url: details.image_url,
          date_creation: details.created_at,
          derniere_mise_a_jour: details.updated_at,
          application_url: details.application_url,
          // Champs requis mais pas forcément disponibles
          environnement_id: details.environment_id,
          image_tag: details.image_tag || 'latest',
          helm_chart_version: '1.0.0',
          certificat_tls_id: null,
          nom_de_domaine: details.domain,
          // Ajouter les champs Odoo depuis l'API
          ...(details.odoo_version && { odoo_version: details.odoo_version }),
          ...(details.odoo_language && { odoo_language: details.odoo_language })
        } as Deployment;
        
        setDeployment(deployment);
        setEnvironmentId(details.environment_id);
        
        // Deployment details loaded successfully
      } catch (error) {
        Logger.error('Failed to load deployment details', error);
        showErrorToast(error, ErrorMessages.DEPLOYMENT_LOAD_FAILED);
      } finally {
        setLoading(false);
      }
    };

    if (deploymentId) {
      fetchDeployment();
    }
  }, [deploymentId]);

  // Vérifier les mises à jour disponibles (Go Live) pour les apps CODE
  useEffect(() => {
    const checkAvailableUpdates = async () => {
      if (!deploymentId || !deploymentDetails || deploymentDetails.is_managed_service) return;
      try {
        const response = await apiService.getDeploymentAvailableUpdates(deploymentId);
        setAvailableUpdates(response.available_updates);
        setCurrentTag(response.current_tag || null);
        setSelectedUpdateIdx(0);
      } catch (error) {
        Logger.error('Failed to check available updates', error);
      }
    };

    checkAvailableUpdates();
  }, [deploymentId, deploymentDetails]);

  // Déterminer le statut basé sur ArgoCD (pour services managés ou fallback)
  const determineHealthFromArgoCD = () => {
    const healthStatus = deploymentDetails?.argocd?.health;
    const syncStatus = deploymentDetails?.argocd?.sync;

    if (!healthStatus && !syncStatus) {
      setAppHealthStatus('starting');
      return;
    }

    // Pour les services managés ou apps non exposées publiquement: Healthy = online
    if (deploymentDetails?.is_managed_service || deploymentDetails?.expose_public === false) {
      if (healthStatus === 'Healthy') {
        setAppHealthStatus('online');
      } else if (healthStatus === 'Degraded' || healthStatus === 'Missing') {
        setAppHealthStatus('offline');
      } else {
        // Progressing, Unknown → démarrage
        setAppHealthStatus('starting');
      }
      return;
    }

    // Pour les apps publiques: Healthy mais pas de réponse HTTP → démarrage
    if (healthStatus === 'Healthy' || syncStatus === 'Synced') {
      setAppHealthStatus('starting');
    } else if (healthStatus === 'Degraded' || healthStatus === 'Missing' || syncStatus === 'OutOfSync') {
      setAppHealthStatus('offline');
    } else {
      setAppHealthStatus('starting');
    }
  };

  // Vérifier la disponibilité de l'application via l'API backend
  const checkApplicationHealth = async () => {
    if (!deploymentId) {
      setAppHealthStatus('offline');
      return;
    }

    // Pour les services managés ou apps non exposées publiquement,
    // utiliser uniquement le statut ArgoCD - pas d'appel HTTP
    if (deploymentDetails?.is_managed_service || deploymentDetails?.expose_public === false) {
      determineHealthFromArgoCD();
      return;
    }

    try {
      const healthData = await apiService.checkDeploymentHealth(deploymentId);

      // Déterminer le statut basé sur le status_code retourné
      if (healthData.status_code && healthData.status_code >= 200 && healthData.status_code < 400) {
        // HTTP 2xx-3xx → En ligne (succès ou redirection)
        setAppHealthStatus('online');
      } else if (healthData.status_code === 502 || healthData.status_code === 503 || healthData.status_code === 504) {
        // HTTP 502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout → Démarrage
        // Nginx retourne ces codes quand les pods ne sont pas encore prêts
        setAppHealthStatus('starting');
      } else if (healthData.status_code && healthData.status_code >= 500) {
        // HTTP 500, 501, 505+ → En ligne (serveur répond mais erreur interne)
        // Odoo retourne souvent 500 lors du premier lancement
        setAppHealthStatus('online');
      } else if (healthData.status_code && healthData.status_code >= 400) {
        // HTTP 400-499 → Indisponible (erreurs client, 404, 403, etc.)
        setAppHealthStatus('offline');
      } else if (!healthData.status_code && healthData.error) {
        // Pas de réponse (timeout, connexion refusée) → Démarrage
        setAppHealthStatus('starting');
      } else {
        // Fallback → Démarrage
        setAppHealthStatus('starting');
      }
    } catch (error: unknown) {
      // En cas d'erreur API, vérifier ArgoCD
      Logger.error('Failed to check deployment health', error);
      determineHealthFromArgoCD();
    }
  };

  // Vérification périodique de la disponibilité
  useEffect(() => {
    if (!deploymentId) return;

    // Première vérification immédiate
    checkApplicationHealth();

    // Vérification toutes les 15 secondes (sauf pour services managés qui utilisent ArgoCD)
    const interval = setInterval(() => {
      checkApplicationHealth();
    }, 15000);

    return () => clearInterval(interval);
  }, [deploymentId, deploymentDetails?.is_managed_service]);

  const refreshDeployment = async () => {
    try {
      const details = await apiService.getDeploymentDetails(deploymentId!);
      setDeploymentDetails(details);

      // Reconstruire l'objet deployment depuis les details fraichement fetches.
      // BUG corrige (2026-05-18) : le spread partiel precedent gardait l'ancien
      // image_tag/image_registry_url/nom_de_domaine en cache, donc apres un
      // Go Live l'edit form se pre-remplissait avec l'ancien tag → rollback
      // silencieux si l'utilisateur sauvegardait sans editer le champ.
      if (deployment) {
        setDeployment({
          ...deployment,
          statut_deploiement: details.status,
          cpu_requis: details.cpu_required,
          ram_requise: details.ram_required,
          storage_requis: details.storage_required,
          replicas: details.replicas,
          derniere_mise_a_jour: details.updated_at,
          image_registry_url: details.image_url,
          image_tag: details.image_tag || 'latest',
          nom_de_domaine: details.domain,
          application_url: details.application_url,
          repo_url: details.repository_url,
          ...(details.odoo_version && { odoo_version: details.odoo_version }),
          ...(details.odoo_language && { odoo_language: details.odoo_language }),
        });
      }
    } catch (error) {
      Logger.error('Failed to refresh deployment data', error);
      showErrorToast(error, ErrorMessages.DEPLOYMENT_LOAD_FAILED);
    }
  };

  const handleScale = async (newReplicas: number) => {
    try {
      await apiService.updateDeployment(deploymentId!, { replicas: newReplicas });
      showSuccessToast(t('management.scaleSuccess'));
      refreshDeployment();
    } catch (error) {
      Logger.error('Failed to scale deployment', error);
      showErrorToast(error, t('management.scaleError'));
    }
  };

  const handleRestart = async () => {
    try {
      await apiService.restartDeployment(deploymentId!);
      showSuccessToast(SuccessMessages.DEPLOYMENT_RESTARTED);
      refreshDeployment();
    } catch (error) {
      Logger.error('Failed to restart deployment', error);
      showErrorToast(error, t('management.restartError'));
    }
  };

  // La suppression emporte desormais la base et ses volumes : un window.confirm
  // ne suffit plus a porter ce que le geste detruit, ni la case de sauvegarde
  // finale.
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Acces temporaire des applications a installation manuelle (n8n, Metabase).
  const [protectionBusy, setProtectionBusy] = useState(false);
  const [nouveauxAcces, setNouveauxAcces] = useState<{ username: string; password: string } | null>(null);

  const basculerProtection = async (enabled: boolean) => {
    if (!deploymentId) return;
    setProtectionBusy(true);
    try {
      const r = await apiService.setSetupProtection(deploymentId, enabled);
      // Un acces remis a un mot de passe NEUF, restitue une seule fois :
      // l'ancien n'est stocke en clair nulle part et a pu circuler depuis.
      setNouveauxAcces(r.protection_credentials ?? null);
      showSuccessToast(t(enabled ? 'management.protectionOn' : 'management.protectionOff') as string);
      refreshDeployment();
    } catch (error) {
      Logger.error('Failed to toggle setup protection', error);
      showErrorToast(error);
    } finally {
      setProtectionBusy(false);
    }
  };

  const handleDelete = () => setDeleteModalOpen(true);

  const confirmDelete = async (finalBackup: boolean) => {
    setDeleting(true);
    try {
      await apiService.deleteDeployment(deploymentId!, finalBackup);
      showSuccessToast(SuccessMessages.DEPLOYMENT_DELETED);
      setDeleteModalOpen(false);
      environmentId ? navigate(`/deployments/${environmentId}`) : navigate('/deployments');
    } catch (error) {
      // Un 409 porte un message actionnable (sauvegarde en cours, protection
      // active) : il faut le montrer tel quel plutot que le message generique.
      Logger.error('Failed to delete deployment', error);
      showErrorToast(error, ErrorMessages.DEPLOYMENT_DELETE_FAILED);
    } finally {
      setDeleting(false);
    }
  };

  /** Coupe ou rallume l'auto-deploiement. Ecrit par l'endpoint du DEPLOIEMENT, le
   *  meme que la configuration de build de NubiBuild : un seul stockage, deux portes.
   *  Desactiver ne transmet PAS la source -- sinon couper reecrirait le depot et la
   *  branche du deploiement. */
  const [majAutoDeploy, setMajAutoDeploy] = useState(false);
  const toggleAutoDeploy = async () => {
    if (!deploymentId) return;
    const cible = !deploymentDetails?.auto_deploy_enabled;
    setMajAutoDeploy(true);
    try {
      await apiService.setAutoDeploy(deploymentId, { enabled: cible });
      showSuccessToast(cible
        ? t('autoDeploy.enabled')
        : t('autoDeploy.disabled'));
      refreshDeployment();
    } catch (error) {
      Logger.error('Bascule de l auto-deploiement', error);
      showErrorToast(error);
    } finally {
      setMajAutoDeploy(false);
    }
  };

  const toggleProtection = async () => {
    if (!deploymentId) return;
    const cible = !deploymentDetails?.deletion_protection;
    try {
      await apiService.setDeletionProtection(deploymentId, cible);
      showSuccessToast(t('deletionProtection.updated') as string);
      refreshDeployment();
    } catch (error) {
      Logger.error('Failed to toggle deletion protection', error);
      showErrorToast(error);

    }
  };

  const handleGoLive = async () => {
    const selected = availableUpdates[selectedUpdateIdx];
    if (!selected || !deploymentId) return;
    setGoingLive(true);
    try {
      // Le dépôt ET le tag. `image_registry_url` est posé à la création et jamais
      // revu, alors qu'un build peut pousser dans un AUTRE dépôt que celui du
      // déploiement (brique NubiStack `stk-*` contre repo connecté) : n'envoyer que
      // le tag greffe une référence qui n'existe pas, et le 200 OK ne le dit pas.
      const depot = selected.image_url ? depotSansTag(selected.image_url) : '';
      await apiService.updateDeployment(deploymentId, {
        image_tag: selected.image_tag,
        ...(depot ? { image_registry_url: depot } : {}),
      });
      showSuccessToast(t('management.goLiveSuccess'));
      setAvailableUpdates([]);
      refreshDeployment();
    } catch (error) {
      Logger.error('Failed to go live', error);
      showErrorToast(error, t('management.goLiveError'));
    } finally {
      setGoingLive(false);
    }
  };

  const handleEditDeployment = () => {
    if (!deployment) return;

    Logger.log('handleEditDeployment - deployment object:', deployment);
    Logger.log('deployment.cpu_requis:', deployment.cpu_requis);
    Logger.log('deployment.ram_requise:', deployment.ram_requise);
    Logger.log('deployment.storage_requis:', deployment.storage_requis);

    const formData = {
      nom: deployment.nom,
      cpu_requis: deployment.cpu_requis,
      ram_requise: deployment.ram_requise,
      storage_requis: deployment.storage_requis,
      replicas: deployment.replicas,
      image_registry_url: deployment.image_registry_url || '',
      image_tag: deployment.image_tag || '',
      nom_de_domaine: deployment.nom_de_domaine || '',
      helm_chart_version: deployment.helm_chart_version
    };

    Logger.log('formData to set in editForm:', formData);
    setEditForm(formData);

    // Charger les variables d'environnement existantes
    const existingEnvVars = deploymentDetails?.environment_variables || {};
    const existingSecrets = deploymentDetails?.sensitive_variables || {};

    // Convertir en tableau de paires clé-valeur
    const envVarEntries: EnvVarEntry[] = Object.entries(existingEnvVars).map(([key, value]) => ({
      id: nextEnvVarId(),
      key,
      value
    }));
    const secretEntries: EnvVarEntry[] = Object.entries(existingSecrets).map(([key, value]) => ({
      id: nextEnvVarId(),
      key,
      value,
      fromServer: true,
    }));

    Logger.log('Loading env vars:', envVarEntries);
    Logger.log('Loading secrets:', secretEntries);

    setEnvVars(envVarEntries);
    setSecrets(secretEntries);

    // Charger l'état d'exposition publique existant
    setExposePublic(deploymentDetails?.expose_public !== false);

    // Charger le port de l'application existant
    const existingPort = (deploymentDetails as any)?.port;
    setAppPort(existingPort || null);

    // Charger le health check path existant (vide = TCP check)
    const existingHealthCheckPath = (deploymentDetails as any)?.health_check_path;
    setHealthCheckPath(existingHealthCheckPath || '');

    // Charger la configuration de migration existante
    const migrationConfig = (deploymentDetails as any)?.migration_enabled !== undefined;
    if (migrationConfig) {
      setMigrationEnabled((deploymentDetails as any)?.migration_enabled || false);
      const cmd = (deploymentDetails as any)?.migration_command;
      setMigrationCommand(commandToString(cmd));
      setMigrationBlockOnFailure((deploymentDetails as any)?.migration_block_on_failure ?? true);
    } else {
      setMigrationEnabled(false);
      setMigrationCommand('');
      setMigrationBlockOnFailure(true);
    }
    setMigrationImageRepository((deploymentDetails as any)?.migration_image_repository || '');
    setMigrationImageTag((deploymentDetails as any)?.migration_image_tag || '');

    // nginx.conf custom
    setNginxCustomConfig((deploymentDetails as any)?.nginx_custom_config || '');
    setNginxDirty(false);

    /* Type de charge et commande : relus du values.yaml git par la fiche (aucune colonne en
       base). Sans ce pré-remplissage l'écran s'ouvrirait vide et le premier enregistrement
       figerait ou effacerait la valeur — le défaut déjà payé sur `extraEnv`.
       `commandToString` est le symétrique de `parseCommand` à l'envoi, le même couple que
       la commande de migration juste au-dessus. */
    setWorkloadType(deploymentDetails?.workload_type || 'web');
    setStartCommand(commandToString(deploymentDetails?.command ?? null));
    setCronSchedule(deploymentDetails?.schedule || '');
    setWorkloadDirty(false);

    setShowEditModal(true);
  };

  const handleUpdateDeployment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deployment) return;

    // Un depot de migration dedie EXIGE son tag. Vide, le chart replie sur le tag de
    // l'APPLICATION : le bon tag d'une AUTRE image, donc une reference bien formee qui
    // n'existe pas -- rien ne peut la refuser ensuite. Et le Job de migration etant un
    // hook PreSync, le pull manque bloque TOUT le sync ArgoCD. L'API refuse aussi (422) ;
    // ici c'est pour le dire sans aller-retour.
    if (migrationEnabled && migrationImageRepository.trim() && !migrationImageTag.trim()) {
      showErrorToast(null, t('management.migrationImageTagRequired'));
      return;
    }

    /* UN WORKER OU UN CRON SANS COMMANDE LANCE L'ENTRYPOINT DE L'IMAGE, c'est-a-dire son
       serveur web -- precisement l'incident qui a motive cette fonctionnalite. Le chart ne
       peut pas le refuser : un `fail` au rendu deviendrait un ComparisonError ArgoCD qui
       arrete TOUS les syncs de l'application. */
    if ((workloadType === 'worker' || workloadType === 'cron') && !startCommand.trim()) {
      showErrorToast(null, t('management.startCommandRequired'));
      return;
    }
    /* UN CRON SANS PLANIFICATION FAIT ECHOUER LE RENDU HELM (`required` sur `schedule`),
       donc ArgoCD n'applique plus rien sur cette application. L'API refuse aussi (422) ;
       ici c'est pour le dire sans aller-retour. */
    if (workloadType === 'cron' && !cronSchedule.trim()) {
      showErrorToast(null, t('management.cronScheduleRequired'));
      return;
    }

    // Un seul enregistrement a la fois. Deux PUT concurrents sur le meme deploiement
    // ecrivent sur la meme branche de nubi-client : GitLab rejette le second
    // (« reference does not point to expected object »), la base est revertee, et le
    // message affiche n'a rien a voir avec la cause. L'API refuse aussi (409, verrou
    // consultatif) ; ici c'est pour que le second clic n'ait simplement pas lieu.
    if (savingEdit) return;
    setSavingEdit(true);

    // Convertir les tableaux de variables en objets Record<string, string>
    const envVarsRecord: Record<string, string> = {};
    envVars.forEach(entry => {
      if (entry.key.trim()) {
        envVarsRecord[entry.key.trim()] = entry.value;
      }
    });

    const secretsRecord: Record<string, string> = {};
    secrets.forEach(entry => {
      if (entry.key.trim()) {
        secretsRecord[entry.key.trim()] = entry.value;
      }
    });

    // Préparer les données à envoyer
    // ENV VARS + SECRETS : toujours envoyer la liste complete (meme vide), comme ca:
    //   - Le user qui supprime UNE entree voit la suppression appliquee (sinon le backend
    //     mergeait avec l'existant et la ressuscitait — "je supprime PORT et il revient")
    //   - Le user qui supprime TOUT peut effectivement tout vider.
    // Cote backend: si le champ est present (meme vide), on REMPLACE. Si absent, preserve.
    const updateData: DeploymentUpdate = {
      ...editForm,
      environment_variables: envVarsRecord,
      // Application managee : une valeur VIDE sur une cle chargee depuis la
      // fiche veut dire « inchange » -- l'API reprend la valeur de git.
      sensitive_variables: secretsRecord,
      // Port de l'application
      ...(appPort !== null && { port: appPort }),
      // Health check path
      health_check_path: healthCheckPath,
      // Configuration migration
      migration_enabled: migrationEnabled,
      migration_command: migrationEnabled && migrationCommand.trim() ? parseCommand(migrationCommand) : null,
      migration_block_on_failure: migrationBlockOnFailure,
      // Image dediee a la migration (vide -> fallback chart sur image principale)
      migration_image_repository: migrationEnabled ? (migrationImageRepository || '') : null,
      migration_image_tag: migrationEnabled ? (migrationImageTag || '') : null,
      // Exposition publique
      expose_public: exposePublic,
      // nginx.conf custom :
      // - dirty=true → on envoie la valeur (même "") pour REPLACE ou WIPE côté backend
      // - dirty=false → on envoie null → backend PRESERVE l'existant du values.yaml
      ...(nginxDirty ? { nginx_custom_config: nginxCustomConfig } : {}),
      /* Type de charge / commande / planification :
         - pas touché → rien n'est envoyé → l'API préserve ce que git porte ;
         - touché → les trois partent, `workload_type` EXPLICITEMENT (y compris 'web'), car
           c'est la seule façon de repasser un worker en app web : une clé omise survivrait
           dans le values.yaml ;
         - commande vidée → `[]`, qui est un ORDRE et non une absence : le chart ne rend
           alors aucune clé `command` et le pod repart sur l'ENTRYPOINT de son image. */
      ...(workloadDirty
        ? {
            workload_type: workloadType,
            command: startCommand.trim() ? parseCommand(startCommand) : [],
            schedule: workloadType === 'cron' ? cronSchedule.trim() : '',
          }
        : {}),
    };

    Logger.log('handleUpdateDeployment - Sending to API:');
    Logger.log('   updateData:', updateData);
    Logger.log('   editForm.cpu_requis:', editForm.cpu_requis);
    Logger.log('   editForm.ram_requise:', editForm.ram_requise);
    Logger.log('   editForm.storage_requis:', editForm.storage_requis);
    Logger.log('   environment_variables:', envVarsRecord);
    Logger.log('   sensitive_variables:', secretsRecord);

    try {
      await apiService.updateDeployment(deployment.id, updateData);
      showSuccessToast(SuccessMessages.DEPLOYMENT_UPDATED);
      setShowEditModal(false);
      setEditForm({});
      setEnvVars([]);
      setSecrets([]);
      // Recharger les donnees
      refreshDeployment();
    } catch (error: unknown) {
      Logger.error('Failed to update deployment', error);
      // Aucune reponse HTTP, OU une reponse de PASSERELLE (502/504) = la requete a
      // ete coupee en vol, PAS un echec : le serveur a tres probablement fini son
      // travail -- base, git et cluster ecrits. Dire « verifiez votre connexion »
      // invite a recliquer, et c'est precisement ce qui casse.
      //
      // Le 504 manquait a cette condition, et c'est le cas le plus probable : nginx
      // coupe a 120 s et REND une reponse, donc `!error.response` est faux. Mesure du
      // 2026-10-11 sur dev -- une mise en service a rendu 504 alors que l'image du
      // deploiement avait bien change.
      const statut = (error as { response?: { status?: number } })?.response?.status;
      const coupeEnVol = statut === undefined || statut === 502 || statut === 504;
      if (coupeEnVol) {
        showErrorToast(null, t('management.updateMaybeApplied'));
        setShowEditModal(false);
        refreshDeployment();
      } else {
        showErrorToast(error, ErrorMessages.DEPLOYMENT_UPDATE_FAILED);
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // Mémoriser le type d'application pour éviter les recalculs inutiles
  // et garantir que le composant se re-rend quand deploymentDetails change
  // IMPORTANT: Ce hook doit être AVANT les early returns pour respecter les règles de React
  const applicationType = useMemo(() => {
    // Priorité 1: Utiliser le type_application de deploymentDetails (source de vérité du backend)
    if (deploymentDetails?.type_application) {
      return deploymentDetails.type_application;
    }

    // Priorité 2: Si on a déjà le type dans deployment, l'utiliser
    if (deployment?.type_application) {
      return deployment.type_application;
    }

    // Priorité 3: Déduire le type à partir de l'image (fallback)
    const imageUrl = deploymentDetails?.image_url || '';
    if (imageUrl.includes('odoo')) return 'odoo';
    if (imageUrl.includes('wordpress')) return 'wordpress';
    if (imageUrl.includes('python')) return 'python';
    if (imageUrl.includes('node')) return 'node';
    if (imageUrl.includes('nginx')) return 'web';

    return t('management.typeUnknown');
  }, [deploymentDetails, deployment]);

  // Fonction wrapper pour compatibilité avec le code existant
  const getApplicationType = () => applicationType;

  // Les variables d'environnement et les secrets se modifient pour une app
  // CODE (ConfigMap + Secret) ET pour une application managee (Keycloak, n8n,
  // Metabase, Open WebUI... : `extraEnv` et Secret dedie de son chart). Les
  // bases et brokers n'en ont pas.
  const CODE_APP_TYPES = ['python', 'nodejs', 'node', 'web', 'nextjs', 'react', 'frontend', 'go', 'java', 'php'];
  const isCodeApp = CODE_APP_TYPES.includes(getApplicationType().toLowerCase());
  const isManagedApplication = !!deploymentDetails?.is_managed_application;

  // Onglets dynamiques selon le type de déploiement
  const tabs = useMemo(() => {
    const tabList: { id: string; label: string; icon: React.ReactNode }[] = [
      { id: 'overview', label: t('management.tabs.overview'), icon: <InformationCircleIcon className="w-4 h-4 inline mr-1" /> },
    ];

    // Logs : CODE apps uniquement (pas managed, pas wordpress)
    if (!deploymentDetails?.is_managed_service && !['wordpress', 'Type inconnu'].includes(applicationType)) {
      tabList.push({ id: 'logs', label: t('management.tabs.logs'), icon: <CommandLineIcon className="w-4 h-4 inline mr-1" /> });
    }

    // Monitoring : toujours
    tabList.push({ id: 'monitoring', label: t('management.tabs.monitoring'), icon: <ChartBarIcon className="w-4 h-4 inline mr-1" /> });

    // Modules : Odoo uniquement
    if (applicationType === 'odoo') {
      tabList.push({ id: 'modules', label: t('management.tabs.modules'), icon: <CubeIcon className="w-4 h-4 inline mr-1" /> });
    }

    // Sauvegardes : Odoo + PostgreSQL
    if (applicationType === 'odoo' || applicationType === 'postgresql') {
      tabList.push({ id: 'backups', label: t('management.tabs.backups'), icon: <CircleStackIcon className="w-4 h-4 inline mr-1" /> });
    }

    // SQL : PostgreSQL uniquement
    if (applicationType === 'postgresql') {
      tabList.push({ id: 'sql', label: t('management.tabs.sql'), icon: <TableCellsIcon className="w-4 h-4 inline mr-1" /> });
    }

    // Console Kafka : Kafka uniquement
    if (applicationType === 'kafka') {
      tabList.push({ id: 'kafka', label: t('management.tabs.kafka'), icon: <ArchiveBoxIcon className="w-4 h-4 inline mr-1" /> });
    }

    // Config files : CODE apps uniquement (chart nubi-code-app >= 0.10.0 requis).
    // Meme filtre que Logs car les managed_service/ERP n'utilisent pas ce chart.
    if (!deploymentDetails?.is_managed_service && !['wordpress', 'Type inconnu'].includes(applicationType)) {
      tabList.push({ id: 'config-files', label: t('management.tabs.configFiles'), icon: <DocumentTextIcon className="w-4 h-4 inline mr-1" /> });
    }

    return tabList;
  }, [applicationType, deploymentDetails?.is_managed_service, t]);

  // Reset vers overview si l'onglet actif n'existe plus
  useEffect(() => {
    if (!tabs.find(tab => tab.id === activeTab)) {
      setActiveTab('overview');
    }
  }, [tabs, activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500" aria-hidden="true"></div>
        <span className="sr-only">{t('management.loadingDeployment')}</span>
      </div>
    );
  }

  if (!deployment) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text-primary">{t('management.deploymentNotFound')}</h1>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 bg-accent-500 hover:bg-accent-700 text-white font-bold py-2 px-4 rounded"
        >
          {t('back', { ns: 'common' })}
        </button>
      </div>
    );
  }

  const getStatusBadge = (status: string | null | undefined) => {
    const baseClasses = "px-2 inline-flex text-xs leading-5 font-semibold rounded-full";
    if (!status) {
      return `${baseClasses} bg-surface-3 text-text-secondary`;
    }
    switch (status.toLowerCase()) {
      case 'deployed':
      case 'active':
        return `${baseClasses} bg-green-500/12 text-green-400`;
      case 'pending':
      case 'building':
        return `${baseClasses} bg-yellow-500/12 text-yellow-400`;
      case 'failed':
      case 'error':
        return `${baseClasses} bg-red-500/12 text-red-400`;
      default:
        return `${baseClasses} bg-surface-3 text-text-secondary`;
    }
  };

  const getRepositoryUrl = () => {
    const appType = getApplicationType();
    
    // Pour les applications ERP (odoo, wordpress), pas d'URL repo client
    // C'est le repo NubiClient qui gère
    if (appType === 'odoo' || appType === 'wordpress') {
      return t('management.naErpApp');
    }
    
    // Pour les applications CODE (python, node, web), chercher l'URL repo client
    // D'abord dans repository_url direct
    if (deploymentDetails?.repository_url && 
        !deploymentDetails.repository_url.includes('nubicloud/nubi-client')) {
      return deploymentDetails.repository_url;
    }
    
    // Puis dans le repo_url de base
    if (deployment?.repo_url && 
        !deployment.repo_url.includes('nubicloud/nubi-client')) {
      return deployment.repo_url;
    }
    
    // Si on ne trouve pas d'URL client spécifique pour une app CODE
    if (appType === 'python' || appType === 'node' || appType === 'web') {
      return t('management.naUrlNotProvided');
    }
    
    return 'N/A';
  };

  return (
    <>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">{deployment.nom}</h1>
            <p className="text-text-secondary mt-2">{t('management.title')}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleEditDeployment}
              className="bg-accent-500 hover:bg-accent-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('edit', { ns: 'common' })}
            </button>
            <button
              onClick={() => environmentId ? navigate(`/deployments/${environmentId}`) : navigate(-1)}
              className="bg-surface-3 hover:bg-surface-4 text-text-primary border border-border-1 font-bold py-2 px-4 rounded"
            >
              {`← ${t('back', { ns: 'common' })}`}
            </button>
          </div>
        </div>

        {/* Barre d'onglets */}
        <div className="mb-6 border-b border-border-1">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-accent-500 text-accent-400'
                    : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-2'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenu par onglet */}
        {activeTab === 'overview' && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations générales */}
          <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                {t('management.generalInfo')}
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.statusLabel')}</dt>
                  <dd className="mt-1">
                    <span className={getStatusBadge(deploymentDetails?.status || deployment.statut_deploiement)}>
                      {deploymentDetails?.status || deployment.statut_deploiement || t('management.statusUnknown')}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.availability')}</dt>
                  <dd className="mt-1">
                    {appHealthStatus === 'checking' && (
                      <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-surface-3 text-text-secondary">
                        <ClockIcon className="w-4 h-4 inline mr-1" /> {t('management.checking')}
                      </span>
                    )}
                    {appHealthStatus === 'online' && (
                      <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-green-500/12 text-green-400">
                        <CheckCircleIcon className="w-4 h-4 inline mr-1" /> {t('management.online')}
                      </span>
                    )}
                    {appHealthStatus === 'starting' && (
                      <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-yellow-500/12 text-yellow-400">
                        <ArrowPathIcon className="w-4 h-4 inline mr-1" /> {t('management.starting')}
                      </span>
                    )}
                    {appHealthStatus === 'offline' && (
                      <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-red-500/12 text-red-400">
                        <XCircleIcon className="w-4 h-4 inline mr-1" /> {t('management.offline')}
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.typeLabel')}</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {getApplicationType()}
                  </dd>
                </div>
                {getApplicationType() === 'odoo' && (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">{t('management.odooVersion')}</dt>
                      <dd className="mt-1 text-sm text-text-primary">
                        {(() => {
                          // Priorité : odoo_version (nouveau champ DB depuis API) > image_tag depuis API > extraction URL
                          if (deploymentDetails?.odoo_version) {
                            return deploymentDetails.odoo_version;
                          }

                          if (deployment.odoo_version) {
                            return deployment.odoo_version;
                          }

                          if (deploymentDetails?.image_tag && deploymentDetails.image_tag !== 'latest') {
                            return deploymentDetails.image_tag;
                          }

                          if (deployment.image_tag && deployment.image_tag !== 'latest') {
                            return deployment.image_tag;
                          }

                          // Fallback: extraire depuis l'URL de l'image
                          const imageUrl = deploymentDetails?.image_url || deployment.image_registry_url || '';
                          const match = imageUrl.match(/:(\d+\.\d+)/);
                          return match ? match[1] : 'N/A';
                        })()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">{t('management.odooLanguage')}</dt>
                      <dd className="mt-1 text-sm text-text-primary">
                        {(() => {
                          const languageMap: { [key: string]: string } = {
                            'fr_FR': 'Français',
                            'en_US': 'English',
                            'es_ES': 'Español',
                            'de_DE': 'Deutsch',
                            'it_IT': 'Italiano'
                          };
                          // Priorité : odoo_language depuis API > fallback
                          const lang = deploymentDetails?.odoo_language || deployment.odoo_language || 'fr_FR';
                          return languageMap[lang] || lang;
                        })()}
                      </dd>
                    </div>
                  </>
                )}
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.repoUrl')}</dt>
                  <dd className="mt-1 text-sm text-text-primary break-all">
                    {getRepositoryUrl()}
                  </dd>
                </div>
                {/* Acces temporaire des applications a installation manuelle.
                    Bloc AUTONOME : il etait niche dans la branche
                    `is_managed_service` (bases et brokers), or n8n et Metabase
                    sont des `is_managed_application` -- le bouton ne s'affichait
                    donc jamais, alors que l'API renvoyait bien son etat.

                    Tant que l'acces est en place, le client s'authentifie DEUX
                    fois a chaque visite. Le retirer est un geste explicite,
                    propose ici plutot que laisse indefiniment. */}
                {deploymentDetails?.setup_protection?.applicable && (
                  <div className="sm:col-span-2">
                    <div className="p-3 bg-surface-0 border border-border-1 rounded-md">
                      <p className="text-xs text-text-secondary mb-2">
                        {deploymentDetails.setup_protection.enabled
                          ? t('management.protectionActiveHelp')
                          : t('management.protectionRemovedHelp')}
                      </p>
                      {nouveauxAcces && (
                        <p className="mb-2 text-xs text-sky-300">
                          {t('management.protectionNewCreds', {
                            user: nouveauxAcces.username,
                            password: nouveauxAcces.password,
                          })}
                        </p>
                      )}
                      {/* `removable === false` : l'application n'a aucun systeme de comptes,
                          cette porte EST son controle d'acces. Proposer de la retirer reviendrait
                          a proposer d'ouvrir l'instance a Internet. Le serveur refuse d'ailleurs
                          en 409 — on n'affiche pas un bouton qui ne peut que produire une erreur.
                          Le remplacement du mot de passe, lui, reste possible. */}
                      {deploymentDetails.setup_protection.removable === false ? (
                        <>
                          <p className="mb-2 text-xs text-amber-300">
                            {t('management.protectionPermanent')}
                          </p>
                          <button
                            type="button"
                            disabled={protectionBusy}
                            onClick={() => basculerProtection(true)}
                            className="px-3 py-1.5 text-xs rounded-md border border-border-1 text-text-primary hover:bg-surface-3 disabled:opacity-50 transition-colors"
                          >
                            {protectionBusy ? t('management.protectionWorking') : t('management.protectionRotate')}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={protectionBusy}
                          onClick={() => basculerProtection(!deploymentDetails.setup_protection!.enabled)}
                          className="px-3 py-1.5 text-xs rounded-md border border-border-1 text-text-primary hover:bg-surface-3 disabled:opacity-50 transition-colors"
                        >
                          {protectionBusy
                            ? t('management.protectionWorking')
                            : deploymentDetails.setup_protection.enabled
                              ? t('management.protectionRemove')
                              : t('management.protectionRestore')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {/* Pour les services managés: afficher host interne + aide connexion */}
                {deploymentDetails?.is_managed_service ? (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-text-tertiary">
                      {t('management.internalHost')}
                    </dt>
                    <dd className="mt-2">
                      <div
                        className="font-mono text-sm text-text-secondary bg-surface-3 px-3 py-2 rounded cursor-pointer hover:bg-surface-3 transition-colors flex items-center justify-between"
                        onClick={() => {
                          navigator.clipboard.writeText(deploymentDetails.internal_host || deployment.nom_de_domaine || '');
                        }}
                        title={t('management.clickToCopy') as string}
                      >
                        <span>{deploymentDetails.internal_host || deployment.nom_de_domaine}</span>
                        <svg className="w-4 h-4 text-text-quaternary ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>

                      {/* Un PostgreSQL qui heberge un Keycloak est couple a lui :
                          sa suppression sera refusee et sa restauration ramenera
                          aussi les royaumes. Le dire ICI evite que le refus
                          surgisse de nulle part, des semaines apres. */}
                      {(deploymentDetails.hosted_apps?.length ?? 0) > 0 && (
                        <div className="mt-3 p-3 bg-sky-500/12 border border-sky-500/30 rounded-md">
                          <p className="text-xs text-sky-300 font-medium mb-1">
                            {t('management.hostsKeycloakTitle', {
                              apps: deploymentDetails.hosted_apps!.map((k) => `${k.label} « ${k.nom} »`).join(', '),
                            })}
                          </p>
                          <ul className="text-xs text-sky-300/90 list-disc list-inside space-y-1">
                            <li>{t('management.hostsKeycloakDelete')}</li>
                            <li>{t('management.hostsKeycloakRestore')}</li>
                          </ul>
                        </div>
                      )}
                      <div className="mt-3 text-xs text-text-tertiary">
                        <p className="font-medium mb-1">{t('management.howToConnect')}</p>
                        <p className="mb-2">{t('management.useHostEnvVars')}</p>
                        {deploymentDetails.type_application === 'postgresql' && (
                          <code className="block p-2 bg-surface-3 rounded text-xs break-all">
                            DATABASE_URL=postgresql://USER:PASSWORD@{deploymentDetails.internal_host || deployment.nom_de_domaine}:5432/DATABASE
                          </code>
                        )}
                        {deploymentDetails.type_application === 'mysql' && (
                          <code className="block p-2 bg-surface-3 rounded text-xs break-all">
                            DATABASE_URL=mysql://USER:PASSWORD@{deploymentDetails.internal_host || deployment.nom_de_domaine}:3306/DATABASE
                          </code>
                        )}
                        {deploymentDetails.type_application === 'mongodb' && (
                          <code className="block p-2 bg-surface-3 rounded text-xs break-all">
                            MONGODB_URI=mongodb://USER:PASSWORD@{deploymentDetails.internal_host || deployment.nom_de_domaine}:27017/DATABASE
                          </code>
                        )}
                        {deploymentDetails.type_application === 'redis' && (
                          <code className="block p-2 bg-surface-3 rounded text-xs break-all">
                            REDIS_URL=redis://{deploymentDetails.internal_host || deployment.nom_de_domaine}:6379
                          </code>
                        )}
                        {deploymentDetails.type_application === 'rabbitmq' && (
                          <code className="block p-2 bg-surface-3 rounded text-xs break-all">
                            RABBITMQ_URL=amqp://USER:PASSWORD@{deploymentDetails.internal_host || deployment.nom_de_domaine}:5672/
                          </code>
                        )}
                      </div>
                    </dd>
                  </div>
                ) : (
                  <>
                    {deploymentDetails?.expose_public !== false && (
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">{t('management.appUrl')}</dt>
                      <dd className="mt-1 text-sm text-text-primary break-all">
                        {deployment.application_url ? (
                          <a
                            href={deployment.application_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-600 hover:text-accent-800 underline flex items-center"
                          >
                            {deployment.application_url}
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                          </a>
                        ) : (
                          <span className="text-text-tertiary italic">{t('management.urlGenerating')}</span>
                        )}
                      </dd>
                    </div>
                    )}
                    {/* URL interne pour communication inter-services */}
                    {deploymentDetails?.internal_host && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-text-tertiary">
                          {t('management.internalService')}
                        </dt>
                        <dd className="mt-2">
                          <div
                            className="font-mono text-sm text-text-secondary bg-surface-3 px-3 py-2 rounded cursor-pointer hover:bg-surface-3 transition-colors flex items-center justify-between"
                            onClick={() => {
                              navigator.clipboard.writeText(deploymentDetails.internal_host || '');
                            }}
                            title={t('management.clickToCopy') as string}
                          >
                            <span>{deploymentDetails.internal_host}</span>
                            <svg className="w-4 h-4 text-text-quaternary ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="mt-2 text-xs text-text-tertiary">
                            {t('management.useHostname')}
                          </p>
                        </dd>
                      </div>
                    )}
                  </>
                )}
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">
                    {getApplicationType() === 'odoo' ? t('management.odooInstanceCount') : t('management.instanceCount')}
                  </dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {deploymentDetails?.replicas || deployment.replicas}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.createdAt')}</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {deploymentDetails?.created_at ?
                      new Date(deploymentDetails.created_at).toLocaleString() :
                      new Date(deployment.date_creation).toLocaleString()
                    }
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.updatedAt')}</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {deploymentDetails?.updated_at ?
                      new Date(deploymentDetails.updated_at).toLocaleString() :
                      (deployment.derniere_mise_a_jour ? new Date(deployment.derniere_mise_a_jour).toLocaleString() : 'N/A')
                    }
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Ressources */}
          <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
            <div className="px-4 py-5 sm:p-6">
              {/* Go Live */}
              {!deploymentDetails?.is_managed_service && (
                <div className="mb-5 bg-green-500/8 border border-green-500/20 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-500/15">
                      <svg className="h-4 w-4 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-green-400">
                      {t('management.goLive')}
                    </p>
                  </div>
                  {currentTag && (
                    <p className="text-xs text-text-tertiary">
                      {t('management.currentTag')} <span className="font-mono text-text-secondary">{currentTag}</span>
                    </p>
                  )}
                  {availableUpdates.length > 0 ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={selectedUpdateIdx}
                        onChange={(e) => setSelectedUpdateIdx(Number(e.target.value))}
                        className="w-full sm:flex-1 min-w-0 text-sm border border-green-500/30 rounded-lg bg-surface-3 text-text-primary px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {availableUpdates.map((update, idx) => (
                          <option key={update.build_id} value={idx}>
                            {update.image_tag} — {new Date(update.completed_at).toLocaleDateString()}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleGoLive}
                        disabled={goingLive}
                        className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold py-2 px-5 rounded-lg whitespace-nowrap transition-colors"
                      >
                        {goingLive ? t('deploy.deploying') : t('management.goLive')}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-text-tertiary">{t('management.noBuildAvailable')}</p>
                  )}
                </div>
              )}

              <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                {t('management.resources')}
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.cpuRequired')}</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {deploymentDetails?.cpu_required || deployment.cpu_requis} cores
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.ramRequired')}</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {deploymentDetails?.ram_required || deployment.ram_requise} GB
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.storagePostgres')}</dt>
                  <dd className="mt-1 text-sm text-text-primary">
                    {deploymentDetails?.storage_required || deployment.storage_requis} GB
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.imageRegistry')}</dt>
                  <dd className="mt-1 text-sm text-text-primary break-all">
                    {deploymentDetails?.image_url || deployment.image_registry_url || 'N/A'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Actions */}
          <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                {t('management.actions')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('management.scale')}
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleScale(deployment.replicas - 1)}
                      disabled={deployment.replicas <= 1}
                      className="bg-red-500 hover:bg-red-700 disabled:bg-surface-3 text-white font-bold py-1 px-2 rounded"
                    >
                      -
                    </button>
                    <span className="px-4 py-1 border border-border-1 rounded bg-surface-3 text-text-primary">{deployment.replicas}</span>
                    <button
                      onClick={() => handleScale(deployment.replicas + 1)}
                      className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-2 rounded"
                    >
                      +
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleRestart}
                  className="w-full bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
                >
                  {t('management.restart')}
                </button>
                {/* LA SOURCE. Un deploiement qui bouge tout seul doit dire pourquoi,
                    et la ou on le regarde -- pas seulement depuis NubiBuild. */}
                <div className="w-full rounded border border-border-1 p-3 text-sm">
                  <div className="font-medium mb-1">{t('autoDeploy.title')}</div>
                  {deploymentDetails?.connected_repository ? (
                    <p className="text-xs text-text-tertiary mb-2 break-all">
                      {deploymentDetails.connected_repository.full_name}
                      {deploymentDetails.git_branch && ` · ${deploymentDetails.git_branch}`}
                    </p>
                  ) : deploymentDetails?.connected_repository_id ? (
                    <p className="text-xs text-text-tertiary mb-2">
                      {t('autoDeploy.repoUnreadable', {
                        id: deploymentDetails.connected_repository_id })}
                    </p>
                  ) : (
                    <p className="text-xs text-text-tertiary mb-2">
                      {t('autoDeploy.noSource')}
                    </p>
                  )}
                  <p className="text-xs text-text-tertiary mb-2">
                    {deploymentDetails?.auto_deploy_enabled
                      ? t('autoDeploy.on')
                      : t('autoDeploy.off')}
                  </p>
                  {/* Activer SANS source ne declencherait jamais rien : le bouton n'est
                      propose que si le deploiement sait quel depot il suit. */}
                  {(deploymentDetails?.connected_repository_id ||
                    deploymentDetails?.auto_deploy_enabled) && (
                    <button
                      onClick={toggleAutoDeploy}
                      disabled={majAutoDeploy}
                      className="w-full border border-border-1 hover:bg-surface-3 disabled:opacity-40 py-1.5 px-3 rounded text-sm"
                    >
                      {deploymentDetails?.auto_deploy_enabled
                        ? t('autoDeploy.disable')
                        : t('autoDeploy.enable')}
                    </button>
                  )}
                </div>
                {/* Protection : geste distinct de la suppression, c'est tout son interet
                    -- separer la decision de l'execution. */}
                <div className="w-full rounded border border-border-1 p-3 text-sm">
                  <div className="font-medium mb-1">{t('deletionProtection.title')}</div>
                  <p className="text-xs text-text-tertiary mb-2">
                    {deploymentDetails?.deletion_protection
                      ? t('deletionProtection.on')
                      : t('deletionProtection.off')}
                  </p>
                  <button
                    onClick={toggleProtection}
                    className="w-full border border-border-1 hover:bg-surface-3 py-1.5 px-3 rounded text-sm"
                  >
                    {deploymentDetails?.deletion_protection
                      ? t('deletionProtection.disable')
                      : t('deletionProtection.enable')}
                  </button>
                </div>
                <button
                  onClick={handleDelete}
                  disabled={deploymentDetails?.deletion_protection}
                  className="w-full bg-red-500 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded"
                >
                  {t('delete', { ns: 'common' })}
                </button>
              </div>
            </div>
          </div>

          {/* Informations supplémentaires */}
          <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                {t('management.additionalInfo')}
              </h3>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.appId')}</dt>
                  <dd className="text-sm text-text-primary font-mono">{deploymentId}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.tlsCertId')}</dt>
                  <dd className="text-sm text-text-primary">{deployment.certificat_tls_id || 'N/A'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-text-tertiary">{t('management.domainName')}</dt>
                  <dd className="text-sm text-text-primary">{deployment.nom_de_domaine || 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
        </>
        )}

        {/* Onglet Logs */}
        {activeTab === 'logs' && (
          <DeploymentLogsSection deploymentId={deploymentId!} migrationEnabled={migrationEnabled} />
        )}

        {/* Onglet Monitoring */}
        {activeTab === 'monitoring' && (
          <MonitoringContent deploymentId={deploymentId!} />
        )}

        {/* Onglet Modules (Odoo) */}
        {activeTab === 'modules' && (
          <OdooModulesSection deploymentId={deploymentId!} />
        )}

        {/* Onglet Sauvegardes */}
        {activeTab === 'backups' && (
          <BackupsSection deploymentId={deploymentId!} />
        )}

        {/* Onglet SQL */}
        {activeTab === 'sql' && (
          <div className="space-y-3">
            <SqlEditorSection deploymentId={deploymentId!} />
            {/* Studio UI is feature-flagged. Mirror of backend
                settings.studio_ui_enabled — when off, the button is not
                rendered and the API responds 404 on /studio/* endpoints. */}
            {(window as { _env_?: { REACT_APP_STUDIO_UI_ENABLED?: string } })._env_?.REACT_APP_STUDIO_UI_ENABLED === 'true' && (
              <StudioSection deploymentId={deploymentId!} />
            )}
          </div>
        )}

        {/* Onglet Console Kafka */}
        {activeTab === 'kafka' && deploymentId && (
          <KafkaConsoleSection deploymentId={deploymentId} />
        )}

        {activeTab === 'config-files' && deploymentId && (
          <ConfigFilesEditor deploymentId={deploymentId} />
        )}

        <DeleteDeploymentModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          deploymentName={deployment?.nom ?? ''}
          dataKind={dataKindDeSuppression(deploymentDetails?.type_application)}
          keycloakDatabase={deploymentDetails?.keycloak_database ?? null}
          loading={deleting}
        />

      {/* Modal d'édition */}
      {showEditModal && deployment && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border border-border-1 w-full max-w-3xl shadow-lg rounded-md bg-surface-2 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-text-primary mb-4">
              {t('management.editDeployment', { name: deployment.nom })}
            </h3>
            <form onSubmit={handleUpdateDeployment}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('name', { ns: 'common' })}</label>
                  <input
                    type="text"
                    value={editForm.nom || ''}
                    onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">CPU</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editForm.cpu_requis || 0}
                      onChange={(e) => setEditForm({ ...editForm, cpu_requis: parseFloat(e.target.value) })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">RAM (GB)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editForm.ram_requise || 0}
                      onChange={(e) => setEditForm({ ...editForm, ram_requise: parseFloat(e.target.value) })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                    />
                  </div>
                  {!deploymentDetails?.is_managed_service && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary">{t('management.storagePostgresGb')}</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="100"
                        value={editForm.storage_requis || 1}
                        onChange={(e) => setEditForm({ ...editForm, storage_requis: parseFloat(e.target.value) })}
                        className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                      />
                    </div>
                  )}
                </div>

                {!deploymentDetails?.is_managed_service && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">
                      {getApplicationType() === 'odoo' ? t('management.odooInstanceCount') : t('management.instanceCount')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={editForm.replicas || 1}
                      onChange={(e) => setEditForm({ ...editForm, replicas: parseInt(e.target.value) })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                    />
                  </div>
                )}

                {!deploymentDetails?.is_managed_service && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('management.imageRegistryUrl')}</label>
                    <input
                      type="text"
                      value={editForm.image_registry_url || ''}
                      onChange={(e) => setEditForm({ ...editForm, image_registry_url: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                    />
                  </div>
                )}

                {!deploymentDetails?.is_managed_service && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('management.imageTag')}</label>
                    <input
                      type="text"
                      value={editForm.image_tag || ''}
                      onChange={(e) => setEditForm({ ...editForm, image_tag: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                    />
                  </div>
                )}

                {/* Domaine : sans objet sans Ingress, donc sans Service. Un worker ou un cron
                    n'en a pas -- le chart rendrait un Ingress pointant un backend inexistant. */}
                {!deploymentDetails?.is_managed_service &&
                 (workloadType === 'web' || workloadType === 'private') && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">{t('management.domainName')}</label>
                    <input
                      type="text"
                      value={editForm.nom_de_domaine || ''}
                      onChange={(e) => setEditForm({ ...editForm, nom_de_domaine: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-border-1 rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                      placeholder="example.com"
                    />
                    <div className="mt-2 p-3 bg-blue-500/12 border border-blue-500/30 rounded-md">
                      <p className="text-xs text-blue-300 font-medium mb-1">
                        {t('management.dnsRequired')}
                      </p>
                      <p className="text-xs text-blue-400">
                        {t('management.dnsInstruction')}
                      </p>
                      <code className="block mt-1 text-xs bg-blue-500/12 text-blue-300 px-2 py-1 rounded font-mono">
                        your-domain.com → zone-nubi-1.nubiecloud.io
                      </code>
                    </div>
                  </div>
                )}

                {/* Section Variables d'environnement - uniquement pour les apps CODE */}
                {(isCodeApp || isManagedApplication) && (
                  <>
                    {/* Variables d'environnement (non-sensibles) */}
                    <div className="mt-4 border-t border-border-1 pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-text-secondary">
                          {t('management.envVars')}
                        </label>
                        <button
                          type="button"
                          onClick={() => setEnvVars([...envVars, { id: nextEnvVarId(), key: '', value: '' }])}
                          className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded"
                        >
                          {t('management.addEnvVar')}
                        </button>
                      </div>
                      {isManagedApplication && (
                        <p className="text-xs text-text-tertiary mb-2">{t('management.managedEnvVarsHelp')}</p>
                      )}
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {envVars.map((entry, index) => (
                          <div key={entry.id} className="flex space-x-2 items-center">
                            <input
                              type="text"
                              value={entry.key}
                              onChange={(e) => {
                                const newVars = [...envVars];
                                newVars[index].key = e.target.value;
                                setEnvVars(newVars);
                              }}
                              placeholder={t('management.keyPlaceholder') as string}
                              className="flex-1 px-2 py-1 text-sm border border-border-1 rounded bg-surface-3 text-text-primary"
                            />
                            <input
                              type="text"
                              value={entry.value}
                              onChange={(e) => {
                                const newVars = [...envVars];
                                newVars[index].value = e.target.value;
                                setEnvVars(newVars);
                              }}
                              placeholder={t('management.valuePlaceholder') as string}
                              className="flex-1 px-2 py-1 text-sm border border-border-1 rounded bg-surface-3 text-text-primary"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newVars = envVars.filter((_, i) => i !== index);
                                setEnvVars(newVars);
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        {envVars.length === 0 && (
                          <p className="text-xs text-text-quaternary italic">{t('management.noEnvVars')}</p>
                        )}
                      </div>
                    </div>

                    {/* Variables sensibles (secrets) */}
                    <div className="mt-4 border-t border-border-1 pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-text-secondary">
                          {t('management.secrets')}
                        </label>
                        <button
                          type="button"
                          onClick={() => setSecrets([...secrets, { id: nextEnvVarId(), key: '', value: '' }])}
                          className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded"
                        >
                          {t('management.addSecret')}
                        </button>
                      </div>
                      {isManagedApplication && (
                        <p className="text-xs text-text-tertiary mb-2">{t('management.managedSecretsHelp')}</p>
                      )}
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {secrets.map((entry, index) => (
                          <div key={entry.id} className="flex space-x-2 items-center">
                            <input
                              type="text"
                              value={entry.key}
                              onChange={(e) => {
                                const newSecrets = [...secrets];
                                newSecrets[index].key = e.target.value;
                                setSecrets(newSecrets);
                              }}
                              placeholder={t('management.secretKeyPlaceholder') as string}
                              className="flex-1 px-2 py-1 text-sm border border-border-1 rounded bg-surface-3 text-text-primary"
                            />
                            <input
                              type="password"
                              value={entry.value}
                              onChange={(e) => {
                                const newSecrets = [...secrets];
                                newSecrets[index].value = e.target.value;
                                setSecrets(newSecrets);
                              }}
                              placeholder={(entry.fromServer && !entry.value
                                ? t('management.secretUnchangedPlaceholder')
                                : t('management.secretValuePlaceholder')) as string}
                              className="flex-1 px-2 py-1 text-sm border border-border-1 rounded bg-surface-3 text-text-primary"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSecrets = secrets.filter((_, i) => i !== index);
                                setSecrets(newSecrets);
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        {secrets.length === 0 && (
                          <p className="text-xs text-text-quaternary italic">{t('management.noSecrets')}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Section Type de charge + commande de démarrage — apps CODE seulement.
                    Le type décide de l'existence d'un Service, donc de la pertinence des
                    trois sections qui suivent (port, health check, domaine). */}
                {isCodeApp && (
                  <div className="border-t border-border-1 pt-4 mt-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <SignalIcon className="w-5 h-5" />
                      <h4 className="text-sm font-medium text-text-primary">{t('management.workloadTitle')}</h4>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {(['web', 'private', 'worker', 'cron'] as const).map((wt) => (
                        <button
                          type="button"
                          key={wt}
                          onClick={() => { setWorkloadType(wt); setWorkloadDirty(true); }}
                          className={`px-3 py-2 text-sm rounded-md border-2 transition-all capitalize ${
                            workloadType === wt
                              ? 'border-accent-500 bg-accent-500/12 text-text-primary'
                              : 'border-border-1 text-text-secondary hover:border-border-2'
                          }`}
                        >
                          {wt}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-text-tertiary">{t('management.workloadHelp')}</p>

                    {/* Meme constructeur qu'a la creation, et c'est le point : une
                        planification se LIT ici telle qu'elle a ete posee la-bas. Le
                        composant derive son etat de la valeur, donc une expression qu'il ne
                        sait pas reconstituer s'affiche en saisie libre AVEC sa valeur --
                        jamais approximee, sinon l'ecran montrerait une planification qui
                        n'est pas celle qui tourne. */}
                    {workloadType === 'cron' && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          {t('management.cronSchedule')} <span className="text-red-500">*</span>
                        </label>
                        <CronScheduleBuilder
                          value={cronSchedule}
                          onChange={(v) => { setCronSchedule(v); setWorkloadDirty(true); }}
                        />
                      </div>
                    )}

                    {/* La commande est proposée pour TOUS les types, pas seulement worker/cron :
                        une app web peut légitimement en porter une (cas réel en production,
                        une image sans CMD). La cacher ici l'effacerait au premier
                        enregistrement, exactement le défaut déjà payé sur `extraEnv`. */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-text-secondary mb-1">
                        {t('management.startCommand')}
                        {workloadType !== 'web' && workloadType !== 'private' && (
                          <span className="text-red-500"> *</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={startCommand}
                        onChange={(e) => { setStartCommand(e.target.value); setWorkloadDirty(true); }}
                        placeholder="celery -A config worker --loglevel=info"
                        className="w-full px-3 py-2 text-sm border border-border-1 rounded-md font-mono bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                      />
                      <p className="mt-1 text-xs text-text-tertiary">{t('management.startCommandHelp')}</p>
                      {/* Aperçu des jetons : `parseCommand` ne gère que les guillemets
                          doubles, montrer le découpage évite la surprise de quoting. */}
                      {startCommand.trim() && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span className="text-xs text-text-quaternary">{t('management.startCommandPreview')}</span>
                          {parseCommand(startCommand).map((jeton, i) => (
                            <code key={i} className="px-1.5 py-0.5 rounded bg-surface-3 border border-border-1 text-xs font-mono text-text-secondary">
                              {jeton}
                            </code>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Section Port - uniquement pour les apps CODE qui ont un Service */}
                {!deploymentDetails?.is_managed_service && getApplicationType() !== 'odoo' &&
                 (workloadType === 'web' || workloadType === 'private') && (
                  <div className="border-t border-border-1 pt-4 mt-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <SignalIcon className="w-5 h-5" />
                      <h4 className="text-sm font-medium text-text-primary">{t('management.appPortTitle')}</h4>
                    </div>
                    <input
                      type="number"
                      value={appPort || ''}
                      onChange={(e) => setAppPort(e.target.value ? parseInt(e.target.value) : null)}
                      min="1"
                      max="65535"
                      placeholder="8000"
                      className="w-full px-3 py-2 text-sm border border-border-1 rounded-md shadow-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500 bg-surface-3 text-text-primary"
                    />
                    <p className="mt-1 text-xs text-text-tertiary">
                      {t('management.appPortHelp')}
                    </p>
                  </div>
                )}

                {/* Section Health Check Path - apps CODE avec Service seulement : le chart ne
                    rend les sondes que si un Service existe. */}
                {!deploymentDetails?.is_managed_service && getApplicationType() !== 'odoo' &&
                 (workloadType === 'web' || workloadType === 'private') && (
                  <div className="border-t border-border-1 pt-4 mt-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <HeartIcon className="w-5 h-5 text-green-500" />
                      <h4 className="text-sm font-medium text-text-primary">{t('management.healthCheckTitle')}</h4>
                    </div>
                    <input
                      type="text"
                      value={healthCheckPath}
                      onChange={(e) => setHealthCheckPath(e.target.value)}
                      placeholder={t('management.healthCheckPlaceholder') as string}
                      className="w-full px-3 py-2 text-sm border border-border-1 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-surface-3 text-text-primary"
                    />
                    <p className="mt-1 text-xs text-text-tertiary">
                      {t('management.healthCheckHelp')}
                    </p>
                  </div>
                )}

                {/* Section nginx.conf custom - uniquement html-static (image contient nginx)
                    et nextjs (sidecar nginx auto-enabled). Cache pour tous les autres frameworks
                    (React/Vue sans sidecar, backends → la ConfigMap ne serait jamais montée). */}
                {!deploymentDetails?.is_managed_service && getApplicationType() !== 'odoo' &&
                 ['html-static', 'nextjs'].includes((deploymentDetails as any)?.framework || '') && (
                  <div className="border-t border-border-1 pt-4 mt-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <CodeBracketIcon className="w-5 h-5 text-slate-500" />
                      <h4 className="text-sm font-medium text-text-primary">{t('management.nginxCustomConfig')}</h4>
                    </div>
                    <textarea
                      value={nginxCustomConfig}
                      onChange={(e) => { setNginxCustomConfig(e.target.value); setNginxDirty(true); }}
                      placeholder={t('management.nginxCustomConfigPlaceholder') as string}
                      rows={8}
                      maxLength={32768}
                      className="w-full px-3 py-2 text-xs border border-border-1 rounded-md shadow-sm focus:outline-none focus:ring-slate-500 focus:border-slate-500 bg-surface-3 text-text-primary font-mono"
                    />
                    <p className="mt-1 text-xs text-text-tertiary">
                      {t('management.nginxCustomConfigHelp')}
                    </p>
                  </div>
                )}

                {/* Section Migration - uniquement pour les apps CODE */}
                {!deploymentDetails?.is_managed_service && getApplicationType() !== 'odoo' && (
                  <div className="border-t border-border-1 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <ArrowPathIcon className="w-5 h-5" />
                        <h4 className="text-sm font-medium text-text-primary">{t('management.dbMigration')}</h4>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={migrationEnabled}
                          onChange={(e) => setMigrationEnabled(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-surface-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        <span className="ml-2 text-xs text-text-tertiary">
                          {migrationEnabled ? t('management.enabled') : t('management.disabled')}
                        </span>
                      </label>
                    </div>

                    {migrationEnabled && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            {t('management.migrationCommand')}
                          </label>
                          <input
                            type="text"
                            value={migrationCommand}
                            onChange={(e) => setMigrationCommand(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-border-1 rounded-md bg-surface-3 text-text-primary focus:ring-purple-500 focus:border-purple-500"
                            placeholder="python manage.py migrate"
                          />
                          <p className="mt-1 text-xs text-text-quaternary">
                            {t('management.migrationExample')}
                          </p>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-surface-3 rounded">
                          <span className="text-xs text-text-secondary">{t('management.blockOnFailure')}</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={migrationBlockOnFailure}
                              onChange={(e) => setMigrationBlockOnFailure(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-surface-3 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            {t('management.migrationImageUrl')}
                          </label>
                          <input
                            type="text"
                            value={migrationImageRepository}
                            onChange={(e) => setMigrationImageRepository(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-border-1 rounded-md bg-surface-3 text-text-primary focus:ring-purple-500 focus:border-purple-500"
                            placeholder="registry.gitlab.com/username/project"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            {t('management.migrationImageTag')}
                          </label>
                          <input
                            type="text"
                            value={migrationImageTag}
                            onChange={(e) => setMigrationImageTag(e.target.value)}
                            className={`w-full px-3 py-2 text-sm border rounded-md bg-surface-3 text-text-primary focus:ring-purple-500 focus:border-purple-500 ${
                              migrationImageRepository.trim() && !migrationImageTag.trim()
                                ? 'border-red-500'
                                : 'border-border-1'
                            }`}
                            placeholder="latest"
                          />
                          {migrationImageRepository.trim() && !migrationImageTag.trim() ? (
                            <p className="mt-1 text-xs text-red-500">
                              {t('management.migrationImageTagRequired')}
                            </p>
                          ) : (
                            <p className="mt-1 text-xs text-text-quaternary">
                              {t('management.migrationImageHelp')}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* PostgreSQL managed : add/edit additional_databases + bouton Sync */}
                {deploymentDetails?.is_managed_service && deploymentDetails.type_application === 'postgresql' && (
                  <AdditionalDatabasesManager
                    deploymentId={deploymentId!}
                    initial={deploymentDetails.additional_databases || []}
                    onSaved={() => window.location.reload()}
                  />
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditForm({});
                    setEnvVars([]);
                    setSecrets([]);
                    setAppPort(null);
                    setHealthCheckPath('');
                    setMigrationEnabled(false);
                    setMigrationCommand('');
                    setMigrationBlockOnFailure(true);
                    setMigrationImageRepository('');
                    setMigrationImageTag('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-text-secondary bg-surface-3 rounded-md hover:bg-surface-3"
                >
                  {t('cancel', { ns: 'common' })}
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingEdit ? t('management.saving') : t('save', { ns: 'common' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </>
  );
};

export default DeploymentManagementPage;